import { useContext } from "react";
import PlanIcon from "app/assets/IconComponent/PlanIocn";
import { getSessionStore } from "app/utils/sessionStore";
import { ProposalTypes } from "app/utils/types";
import { ThemeContext } from "app/uicomponents/macrocomponents/RootLayout";
import { ProgressBar } from "primereact/progressbar";
import HexagonWithFlag from "app/assets/IconComponent/HexagonWithFlag";
import { getThemeColors } from "app/utils/utils";

interface PhaseButton {
  label: string;
  current: number;
  total: number;
  progress?: number; // 0-100 — uses (current/total)*100 as fallback
  isActive?: boolean;
}

interface PlanPhasesCardProps {
  // planType?: string;
  effectiveDate?: string;
  progressPercent?: number; // kept for legacy callers
  phases?: PhaseButton[];
  planSubType?: string;
  lastValuationDate?: string;
  valuation?: string;
}

const MOCK_PLAN: PlanPhasesCardProps = {
  // planType: "Emerging Affluent",
  planSubType: "Emerging Affluent",
  effectiveDate: "Sep 25, 2024",
  progressPercent: 20,
  phases: [
    { label: "FUNDING", current: 2, total: 10, progress: 20, isActive: true },
    { label: "GROWTH", current: 0, total: 5, progress: 0, isActive: false },
  ],
};

const PlanPhasesCard = ({
  // planType = MOCK_PLAN.planType,
  effectiveDate = MOCK_PLAN.effectiveDate,
  progressPercent = MOCK_PLAN.progressPercent!,
  planSubType = MOCK_PLAN.planSubType,
  phases = MOCK_PLAN.phases!,
  lastValuationDate,
  valuation,
}: PlanPhasesCardProps) => {
  const { isDarkMode } = useContext(ThemeContext);
  const sessionPlanType = getSessionStore("planInfo")?.planType;
  const isDark = isDarkMode;

  const { branding } = getSessionStore("lookUpResponse") ?? {};
  const { primaryColor } = getThemeColors(branding);
  // Only apply branding color when primaryColor is present; otherwise fall back to originals
  const brandColor = primaryColor || null;
  const iconFill = isDark ? "#93C5FD" : brandColor ?? "#1E3A8A";
  const progressFillActive = brandColor ?? "#004781";
  const valuationGradient = brandColor
    ? `linear-gradient(135deg, ${brandColor} 0%, ${brandColor}99 100%)`
    : "linear-gradient(135deg, #004781 0%, #1581ED 100%)";

  const cardBase = `h-full rounded-lg overflow-hidden shadow-lg pb-1 pt-0 ${
    isDark ? "bg-[#2A2A3D]" : "bg-white"
  }`;

  const labelClass = `text-[11px] font-medium  tracking-wide ${
    isDark ? "text-gray-400" : "text-[#6B7280]"
  }`;

  const valueClass = `text-[15px] !font-normal conthrax-font mt-[2px] ${
    isDark ? "text-white" : "text-[#0F172A]"
  }`;

  const sectionHeaderClass = `text-[13px] p-4 font-semibold conthrax-font flex items-center gap-2 mb-3  ${
    isDark ? "bg-[#1E1E2E]" : "bg-[#F5F5F5]"
  } ${isDark ? "text-[#93C5FD]" : "text-[#1E3A8A]"}`;

  const fundingPhase = phases.find((p) => p.label.toUpperCase() === "FUNDING") ?? phases[0];
  const growthPhase = phases.find((p) => p.label.toUpperCase() === "GROWTH") ?? phases[1];

  const phaseProgress = (ph: PhaseButton) =>
    ph?.progress ?? (ph?.total ? Math.floor((ph.current / ph.total) * 100) : progressPercent ?? 0);

  const isFundingActive = phaseProgress(fundingPhase) > 0;
  const isGrowthActive = phaseProgress(growthPhase) > 0;

  const formatPlanSubType = (value: string) => {
    if (!value) return "";
    // Remove TYPE_1_, TYPE_2_, TYPE_3_ prefix and format the rest
    const withoutPrefix = value.replace(/^TYPE_\d+_/, "");
    return withoutPrefix
      .split("_")
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(" ");
  };

  if (sessionPlanType === ProposalTypes.BuySell) {
    return (
      <div className={`${cardBase} w-1/2`}>
        <p className={sectionHeaderClass}>
          <PlanIcon fill={iconFill} size="21px" />
          Plan
        </p>

        <div className="flex gap-4 px-4 pb-4">
          <div className="flex-1">
            <p className={labelClass}>Type</p>
            <p className={valueClass}>Entity Purchase</p>
          </div>

          <div className="flex-1">
            <p className={labelClass}>Last Valuation Date</p>
            <p className={valueClass}>{lastValuationDate || "—"}</p>
            <div
              className="mt-3 rounded-xl px-3 py-2 text-center"
              style={{ background: valuationGradient }}
            >
              <p className="text-white text-[10px] uppercase tracking-wide font-medium">
                Valuation
              </p>
              <p className="text-white text-[20px] conthrax-font">{valuation || "0"}</p>
            </div>
          </div>

          <div className="flex-1">
            <p className={labelClass}>Plan Effective Date</p>
            <p className={valueClass}>{effectiveDate || "—"}</p>
          </div>
        </div>
      </div>
    );
  }

  if (sessionPlanType === ProposalTypes.KeyPersonInsurance) {
    return (
      <div className={`${cardBase} w-1/2`}>
        <p className={sectionHeaderClass}>
          <PlanIcon fill={isDark ? "#93C5FD" : "#1E3A8A"} size="21px" />
          Plan
        </p>

        <div className="flex gap-4 px-4 pb-4">
          <div className="flex-1">
            <p className={labelClass}>Last Assessment</p>
            <p className={valueClass}>{lastValuationDate || "—"}</p>
          </div>

          <div className="flex-1">
            <div
              className="rounded-xl px-3 py-2 text-center"
              style={{ background: valuationGradient }}
            >
              <p className="text-white text-[10px] uppercase tracking-wide font-medium">
                EXPOSURE VALUE
              </p>
              <p className="text-white text-[20px] conthrax-font">{valuation || "0"}</p>
            </div>
          </div>

          <div className="flex-1">
            <p className={labelClass}>Plan Effective Date</p>
            <p className={valueClass}>{effectiveDate || "—"}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-4 w-full mt-4">
      {/* ── Plan card ── */}
      <div className={`${cardBase} w-1/2 !pb-[54px]`}>
        <p className={sectionHeaderClass}>
          <PlanIcon fill={isDark ? "#93C5FD" : "#1E3A8A"} size="21px" />
          Plan
        </p>

        <div className="flex gap-6 px-4 py-1">
          <div>
            <p className={labelClass}>Type</p>
            <p className={valueClass}>{planSubType ? formatPlanSubType(planSubType) : ""}</p>
          </div>
          <div>
            <p className={labelClass}>Effective Date</p>
            <p className={valueClass}>{effectiveDate}</p>
          </div>
        </div>
      </div>

      {/* ── Phases card ── */}
      <div className={`${cardBase} w-1/2`}>
        <p className={sectionHeaderClass}>
          <HexagonWithFlag fill={isDark ? "#93C5FD" : "#1E3A8A"} width={21} height={21} />
          Phases
        </p>

        <div className="w-full flex flex-row gap-3 px-4 py-1 align-items-center">
          {/* Funding */}
          <div className="w-8 flex flex-column">
            <div
              className={`w-full mb-2 border-1 p-1 rounded-lg ${
                isFundingActive
                  ? isDark
                    ? "border-blue-500"
                    : ""
                  : isDark
                  ? "border-gray-600"
                  : "border-gray-300"
              }`}
              style={
                isFundingActive && !isDark ? { borderColor: brandColor ?? "#1D4ED8" } : undefined
              }
            >
              <ProgressBar
                value={phaseProgress(fundingPhase)}
                className="h-2rem text-xs conthrax-font"
                pt={{
                  root: {
                    style: {
                      background: isFundingActive
                        ? isDark
                          ? "#1E2A4D"
                          : `${brandColor ?? "#1E3A8A"}20`
                        : isDark
                        ? "#2E2E42"
                        : "#E5E7EB",
                    },
                  },
                  value: {
                    style: { background: isFundingActive ? progressFillActive : "#9CA3AF" },
                  },
                }}
              />
            </div>
            <p
              className={`sans-font font-semibold uppercase border-1 text-sm py-2 text-center rounded-lg ${
                isDark ? "text-[#93C5FD] border-[#1D4ED8] bg-[#1E2A3D]" : ""
              }`}
              style={
                !isDark
                  ? {
                      color: brandColor ?? "#3B82F6",
                      borderColor: brandColor ? `${brandColor}40` : "#BFDBFE",
                      backgroundColor: brandColor ? `${brandColor}0D` : "#EFF6FF",
                    }
                  : undefined
              }
            >
              Funding {fundingPhase?.current ?? 0}/{fundingPhase?.total ?? 0}
            </p>
          </div>

          {/* Growth */}
          <div className="w-4 flex flex-column">
            <div
              className={`w-full mb-2 border-1 p-1 rounded-lg ${
                isGrowthActive
                  ? isDark
                    ? "border-blue-500"
                    : "border-blue-700"
                  : isDark
                  ? "border-gray-600"
                  : "border-gray-300"
              }`}
            >
              <ProgressBar
                value={phaseProgress(growthPhase)}
                className="h-2rem conthrax-font"
                pt={{
                  root: {
                    style: {
                      background: isGrowthActive
                        ? isDark
                          ? "#1E2A4D"
                          : `${brandColor ?? "#1E3A8A"}20`
                        : isDark
                        ? "#2E2E42"
                        : "#E5E7EB",
                    },
                  },
                  value: { style: { background: isGrowthActive ? progressFillActive : "#9CA3AF" } },
                }}
              />
            </div>
            <p
              className={`sans-font font-semibold uppercase border-1 text-sm py-2 text-center rounded-lg ${
                isDark
                  ? "text-gray-400 border-gray-600 bg-[#1E1E2E]"
                  : "text-gray-500 border-gray-500 bg-gray-50"
              }`}
            >
              Growth {growthPhase?.current ?? 0}/{growthPhase?.total ?? 0}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanPhasesCard;
